/**
 * UI localization strings
 * @module lang
 */

import methods from './methods'

const lang = {
  yes: 'Да',
  no: 'Не',
  exportToCsv: 'Изтегли като CSV',
  personnel: 'Музиканти',
  deleteLocalData: 'Изтриване на настройките',
  deleteLocalDataDialogText: 'Сигурни ли сте, че искате да изтриете локално запазените настройки за сайта?',
  albumDetailsHeading: 'Детайли за албума',
  medium: 'Носител',
  catalogNumber: 'Каталожен номер',
  label: 'Издател',
  viewCode: 'Вижте кода',
  apiRoutes: 'API Routes',
  repositories: 'Git Repos',
  errorDialogTitle: 'Грешка',
  errorDialogText: '<p>Не е налична информация за заявената страница или е възникнала сървърна грешка при зареждането ѝ.</p><p>Моля, опитайте друга връзка от началната страница.</p>',
  api: 'API',
  toStartPage: 'Към началната страница',
  httpMethods: 'HTTP Methods',
  getPayload: 'Извикай метода',
  welcome: 'Добре дошли!',
  colorScheme: 'Изберете цветова схема',
  emailUs: 'Пишете ни',
  followUsOnFacebook: 'Харесайте страницата ни във Фейсбук',
  search: 'Търсене',
  start: 'Начало',
  alpha: 'По азбучен ред',
  id: 'ID',
  name: 'Име',
  genre: 'Стил',
  genres: 'По стил',
  region: 'Регион',
  years: 'По години',
  byRegion: 'По региони',
  releases: 'Издания',
  releaseDate: 'Дата на издаване',
  discography: 'Дискография',
  created: 'Година на създаване',
  moreInfo: 'Кликни за повече информация',
  noDataAvailable: 'Не са открити данни',
  indexInfo: 'Информация за заглавната страница.',
  close: 'Затвори',
  darkTheme: 'Тъмна тема',
  settings: 'Настройки',
  currentMembers: 'Актуален състав',
  previousMembers: 'Предишни членове',
  otherBands: 'участие в други групи',
  bandsParticipation: 'Групи',
  help: 'Помощ',
  helpInfo: 'Полезна информация за сайта и как да ни изпращате групи и изпълнители.',
  regions: 'Региони в България',
  regionsInfo: 'Кликнете върху избрания от Вас регион, за да видите информация за групите в него.',
  alphaNumeric: 'Информация за групите в България по азбучен ред',
  genresInfo: 'Кликнете върху избрания от Вас стил, за да видите българските групи, свирещи в него.',
  /**
   * @function recordsNotFound
   * @param {string} about [about='']
   * @return {string}
   */
  recordsNotFound: (about = '') => {
    return `Не са налични данни за ${about.toLowerCase()}.`
  },
  /**
   * @function apiInfo
   * @param {string} author [author='']
   * @param {string} email [email='']
   * @param {object} links [links={}]
   * @return {string}
   */
  apiInfo: (author = '', email = '', links = {}) => {
    return `Програматичното API за Български рок архив е създадено от <a href="${links.website}" class="external-link" target="_blank">${author}</a>.
Последвайте го в <a class="external-link" href="${links.twitter}" target="_blank">Twitter</a>, <a class="external-link" href="${links.linkedin}" target="_blank">LinkedIn</a> или 
<a class="external-link" href="${links.facebook}" target="_blank">Facebook</a>, четете <a class="external-link" href="${links.blog}" target="_blank">блога</a> му 
или <a href="mailto:${email}" class="external-link" target="_blank">пишете</a>.`
  },
  /**
   * @function labelReleases
   * @param {string} label [string='']
   * @return {string}
   */
  labelReleases: (label = '') => {
    return `Издания на ${decodeURIComponent(label)}.`
  },
  /**
   * @function volume
   * @param {object} details [details={}]
   * @return {string}
   */
  albumDetails: (details = {}) => {
    return `Формат: <strong>${details.medium}</strong> | 
Издател: <strong>${details.label}</strong> | 
Дата на издаване: <strong>${methods.methods.simpleDateFormat(details.date)}</strong>`
  },
  /**
   * @function volume
   * @param {number} volume [volume=undefined]
   * @return {string}
   */
  volume: (volume) => {
    return `Част ${volume + 1}`
  },
  /**
   * @function viewMoreInfo
   * @param {string} about [about=undefined]
   * @return {string}
   */
  viewMoreInfo: (about) => {
    return `Виж повече информация за ${about}`
  },
  /**
   * @function moreInfoAboutTheBandsInRegion
   * @param {string} region [about='']
   * @return {string}
   */
  moreInfoAboutTheBandsInRegion: (region = '') => {
    const preposition = region.charAt(0).toLowerCase() === 'в' ? 'във' : 'в'
    return `Виж информация за групите ${preposition} ${region}`
  },
  /**
   * @function moreInfoAboutTheBandsInGenre
   * @param {string} genre [genre='']
   * @return {string}
   */
  moreInfoAboutTheBandsInGenre: (genre = '') => {
    return `Виж информация за групите, свирещи ${genre}`
  },
  /**
   * @function genreInfo
   * @param {string|null} genre [genre=null]
   * @return {string}
   */
  genreInfo: (genre = null) => {
    return `Информация за българските групи, свирещи ${genre}.`
  },
  /**
   * @function yearsInfo
   * @param {string|null} range [range=null]
   * @return {string}
   */
  yearsInfo: (range = null) => {
    return `Информация за българските групи в периода ${range}.`
  },
  /**
   * @function alphaNumericInfo
   * @param {string|null} letter [range='A']
   * @return {string}
   */
  alphaNumericInfo: (letter = 'A') => {
    letter = decodeURIComponent(letter)
    return `Избраната от Вас буква или цифра е "<strong>${letter}</strong>". 
Кликнете върху избраната от Вас група или изпълнител, за да видите повече информация.`
  },
  /**
   * @function regionInfo
   * @param {string|null} region [region=null]
   * @return {string}
   */
  regionInfo: (region = null) => {
    return `Групи от ${region}.`
  }
}

export default lang



// WEBPACK FOOTER //
// ./src/helpers/lang.js